<script setup>
import {defineProps, ref, computed, onMounted, watch, defineModel, defineExpose, defineEmits} from 'vue'
import {debounce} from '@shared-utils/debounce'
import {useNotification} from '@kyvg/vue3-notification'
import {useBigoBusinessManagerStore} from '@/views/assets/business-managers/stores/BigoBusinessManagerStore'
import {useBingBusinessManagerStore} from '@/views/assets/business-managers/stores/BingBusinessManagerStore'
import {useGoogleBusinessManagerStore} from '@/views/assets/business-managers/stores/GoogleBusinessManagerStore'
import {useNewsbreakBusinessManagerStore} from '@/views/assets/business-managers/stores/NewsbreakBusinessManagerStore'
import {useOutbrainBusinessManagerStore} from '@/views/assets/business-managers/stores/OutbrainBusinessManagerStore'
import {useRevcontentBusinessManagerStore} from '@/views/assets/business-managers/stores/RevcontentBusinessManagerStore'
import {useSnapchatBusinessManagerStore} from '@/views/assets/business-managers/stores/SnapchatBusinessManagerStore'
import {useTaboolaBusinessManagerStore} from '@/views/assets/business-managers/stores/TaboolaBusinessManagerStore'
import {useTiktokBusinessManagerStore} from '@/views/assets/business-managers/stores/TiktokBusinessManagerStore'
import {useMetaBusinessManagerStore} from '@/views/ad-account-requests/accounts/meta/MetaBusinessManagerStore'
import InputPreloader from '@shared-components/ui/inputs/InputPreloader.vue'
import TheSelectMultiple from '@shared-components/ui/selects/TheSelectMultiple.vue'
import TheSelect from '@shared-components/ui/selects/TheSelect.vue'
import {isEqual} from 'lodash'
import {useSmartnewsBusinessManagerStore} from '@/views/assets/business-managers/stores/SmartnewsBusinessManagerStore'
import {usePinterestBusinessManagerStore} from '@/views/assets/business-managers/stores/PinterestBusinessManagerStore'

const {notify} = useNotification()
const {getBigoBusinessManagers} = useBigoBusinessManagerStore()
const {getBingBusinessManagers} = useBingBusinessManagerStore()
const {getGoogleBusinessManagers} = useGoogleBusinessManagerStore()
const {getNewsbreakBusinessManagers} = useNewsbreakBusinessManagerStore()
const {getOutbrainBusinessManagers} = useOutbrainBusinessManagerStore()
const {getRevcontentBusinessManagers} = useRevcontentBusinessManagerStore()
const {getSnapchatBusinessManagers} = useSnapchatBusinessManagerStore()
const {getTaboolaBusinessManagers} = useTaboolaBusinessManagerStore()
const {getTiktokBusinessManagers} = useTiktokBusinessManagerStore()
const {getFacebookBusinessManagers} = useMetaBusinessManagerStore()
const {getSmartnewsBusinessManagers} = useSmartnewsBusinessManagerStore()
const {getPinterestBusinessManagers} = usePinterestBusinessManagerStore()

const emit = defineEmits(['focusout', 'change'])

const modelValue = defineModel({required: true})
const props = defineProps({
  platform: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: 'BM - Enter From 2 Characters to Search',
  },
  selectedBusinessManager: {},
  listBySearch: {
    type: Boolean,
    default: false,
  },
  withAppended: {
    type: Boolean,
    default: true,
  },
  payload: {
    type: Object,
    default: null,
  },
  mode: {
    type: String,
    default: 'single',
  },
})

// Refs
const businessManagers = ref([])
const loadingBM = ref(false)
const fullSearchResultList = ref([])
let previousPayload = ref()
const platformHandlers = {
  bigo: getBigoBusinessManagers,
  bing: getBingBusinessManagers,
  google: getGoogleBusinessManagers,
  newsbreak: getNewsbreakBusinessManagers,
  outbrain: getOutbrainBusinessManagers,
  revcontent: getRevcontentBusinessManagers,
  snapchat: getSnapchatBusinessManagers,
  taboola: getTaboolaBusinessManagers,
  tiktok: getTiktokBusinessManagers,
  facebook: getFacebookBusinessManagers,
  meta: getFacebookBusinessManagers,
  smartnews: getSmartnewsBusinessManagers,
  pinterest: getPinterestBusinessManagers,
}

// Computed
const formatBusinessManagerTitle = (businessManager, withCount = true) => {
  const idPart = businessManager.external_id ? ` ID: ${businessManager.external_id}` : ''
  const adAccountPart = ` (${businessManager.not_draft_ad_accounts_count})`

  return withCount ? businessManager.title + idPart + adAccountPart : businessManager.title + idPart
}

const businessManagersOptions = computed(() =>
  businessManagers.value.map(businessManager => ({
    value: businessManager.hash,
    label: formatBusinessManagerTitle(businessManager),
  })),
)

// Methods
onMounted(async () => {
  await init()
})

const init = async () => {
  if (props.selectedBusinessManager?.hash !== null) {
    await handleSelectedBusinessManager()

    return
  }

  if (!props.listBySearch) {
    return
  }

  await requestGetBusinessManagers()
}

const getBusinessManagers = async search => {
  const platform = props?.platform
  const params = {search}

  const companyHash = props?.payload?.company_hash
  if (companyHash !== null && companyHash !== undefined) {
    params.company_hash = companyHash
  }

  const handler = platformHandlers[platform]
  if (handler) {
    return await handler(params)
  }
}

const handleSelectedBusinessManager = async () => {
  loadingBM.value = true

  await requestGetBusinessManagers(props.selectedBusinessManager?.title || props.selectedBusinessManager?.external_id)
}

const requestGetBusinessManagers = async search => {
  try {
    let response = await getBusinessManagers(search)
    businessManagers.value = response.data

    if (search === '  ') {
      fullSearchResultList.value = cloneSafe(businessManagers.value)
    }
  } catch (responseError) {
    if (responseError?.data !== undefined) {
      notify({type: 'error', text: responseError?.data.message})
    }
  } finally {
    loadingBM.value = false
  }
}

const handleSearch = value => {
  checkSearchValue(value)
}

const checkSearchValue = async searchValue => {
  if (searchValue.length >= 2) {
    await reloadBusinessManagers(searchValue)
  }
}

const reloadBusinessManagers = debounce(async searchValue => {
  await requestGetBusinessManagers(searchValue)
}, 300)

const onFocusOut = () => {
  emit('focusout')
  handleSearch('')
}
const onChange = () => {
  emit('change')
}

watch(
  () => props.selectedBusinessManager,
  async () => {
    if (props.selectedBusinessManager?.hash !== null) {
      await handleSelectedBusinessManager()
    }
  },
)

watch(
  () => props.payload,
  newPayload => {
    if (!isEqual(newPayload, previousPayload)) {
      previousPayload = cloneSafe(newPayload)
      fullSearchResultList.value.length = 0
    }
  },
  {deep: true},
)

defineExpose({handleSearch})
</script>

<template>
  <input-preloader :show-preloader="loadingBM" name="Business Manager">
    <span v-bind="$attrs">
      <the-select
        v-if="props.mode === 'single'"
        :class="[modelValue && props.withAppended ? 'with-appended' : '']"
        v-model="modelValue"
        :options="businessManagersOptions"
        :disabled="disabled || loadingBM"
        @search-change="handleSearch"
        @focusout="onFocusOut"
        @change="onChange"
        searchable
        :placeholder="placeholder"
        :allow_clear="false"
        :preselect="false"
        close-on-deselect
        mode="single"
        required
      />
      <the-select-multiple
        v-else
        :class="[modelValue && props.withAppended ? 'with-appended' : '']"
        v-model="modelValue"
        :options="businessManagersOptions"
        :disabled="disabled || loadingBM"
        @search-change="handleSearch"
        @focusout="onFocusOut"
        @change="onChange"
        searchable
        :placeholder="placeholder"
        :allow_clear="false"
        :mode="mode"
        required
      />
    </span>
    <slot></slot>
  </input-preloader>
</template>
